// §34.3 - shop bays settings: add / rename inline / reorder / deactivate /
// reactivate. Matches VehiclesTab.tsx's click-to-edit + toast pattern.
// When multi-location is gated on and there are 2+ active locations, each bay
// also carries a location select (create/edit); otherwise bays render exactly
// as before and omit locationId from payloads.
import { useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { Bay, Location } from '@wrapforce/shared';
import { apiErrorMessage } from '@/lib/api-errors';
import { PLAN_DEFINITIONS } from '@wrapforce/shared/constants';
import { useBays, useCreateBay, useUpdateBay } from '@/features/scheduling/api';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useLocations } from '@/features/locations/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectItem } from '@/components/ui/select';
import { cn } from '@/lib/cn';

export function BaysSettings({ workspaceId }: { workspaceId: string | null }) {
  const { t } = useTranslation('settings');
  const { data: bays = [] } = useBays(workspaceId);
  const createBay = useCreateBay(workspaceId);
  const updateBay = useUpdateBay(workspaceId);
  const { membership } = useActiveMembership();
  const { data: locationsData } = useLocations(workspaceId);
  const [newName, setNewName] = useState('');
  const [newLocationId, setNewLocationId] = useState('');

  const sorted = [...bays].sort((a, b) => a.sort - b.sort);
  const allLocations = locationsData?.items ?? [];
  const activeLocations = allLocations.filter((l) => l.isActive);
  const multiLocationOn = membership ? PLAN_DEFINITIONS[membership.plan].gates.multiLocation : false;
  const showLocations = multiLocationOn && activeLocations.length >= 2;
  const selectedNewLocationId = newLocationId || activeLocations[0]?.id || '';

  async function handleAdd() {
    const name = newName.trim();
    if (!name) return;
    try {
      await createBay.mutateAsync({
        name,
        sort: sorted.length ? Math.max(...sorted.map((b) => b.sort)) + 1 : 0,
        ...(showLocations && selectedNewLocationId ? { locationId: selectedNewLocationId } : {}),
      });
      setNewName('');
      toast.success(t('bays.addedToast'));
    } catch (err) {
      toast.error(t('bays.saveError'), { description: apiErrorMessage(err) });
    }
  }

  async function handleRename(bay: Bay, name: string) {
    if (!name.trim() || name === bay.name) return;
    try {
      await updateBay.mutateAsync({ bayId: bay.id, input: { name: name.trim() } });
    } catch (err) {
      toast.error(t('bays.saveError'), { description: apiErrorMessage(err) });
    }
  }

  async function handleToggleActive(bay: Bay) {
    try {
      await updateBay.mutateAsync({ bayId: bay.id, input: { isActive: !bay.isActive } });
      toast.success(bay.isActive ? t('bays.deactivatedToast') : t('bays.reactivatedToast'));
    } catch (err) {
      toast.error(t('bays.saveError'), { description: apiErrorMessage(err) });
    }
  }

  async function handleChangeLocation(bay: Bay, locationId: string) {
    if (!locationId || locationId === bay.locationId) return;
    try {
      await updateBay.mutateAsync({ bayId: bay.id, input: { locationId } });
    } catch (err) {
      toast.error(t('bays.saveError'), { description: apiErrorMessage(err) });
    }
  }

  async function handleMove(index: number, direction: -1 | 1) {
    const other = sorted[index + direction];
    const current = sorted[index];
    if (!other || !current) return;
    try {
      await Promise.all([
        updateBay.mutateAsync({ bayId: current.id, input: { sort: other.sort } }),
        updateBay.mutateAsync({ bayId: other.id, input: { sort: current.sort } }),
      ]);
    } catch (err) {
      toast.error(t('bays.saveError'), { description: apiErrorMessage(err) });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('bays.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-2">
          {sorted.map((bay, index) => (
            <BayRow
              key={bay.id}
              bay={bay}
              canMoveUp={index > 0}
              canMoveDown={index < sorted.length - 1}
              showLocations={showLocations}
              locations={allLocations}
              activeLocations={activeLocations}
              onRename={(name) => handleRename(bay, name)}
              onToggleActive={() => handleToggleActive(bay)}
              onChangeLocation={(locationId) => handleChangeLocation(bay, locationId)}
              onMoveUp={() => handleMove(index, -1)}
              onMoveDown={() => handleMove(index, 1)}
            />
          ))}
          {sorted.length === 0 ? <p className="text-sm text-muted">{t('bays.empty')}</p> : null}
        </div>

        <div className="flex items-center gap-2 border-t border-border pt-3">
          <Input
            placeholder={t('bays.addPlaceholder')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            className="max-w-xs"
          />
          {showLocations ? (
            <Select value={selectedNewLocationId} onValueChange={setNewLocationId}>
              {activeLocations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
              ))}
            </Select>
          ) : null}
          <Button size="sm" variant="secondary" className="gap-1.5" disabled={!newName.trim()} loading={createBay.isPending} onClick={handleAdd}>
            <Plus size={13} /> {t('bays.add')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function BayRow({
  bay, canMoveUp, canMoveDown, showLocations, locations, activeLocations, onRename, onToggleActive, onChangeLocation, onMoveUp, onMoveDown,
}: {
  bay: Bay;
  canMoveUp: boolean;
  canMoveDown: boolean;
  showLocations: boolean;
  locations: Location[];
  activeLocations: Location[];
  onRename: (name: string) => void;
  onToggleActive: () => void;
  onChangeLocation: (locationId: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const { t } = useTranslation('settings');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(bay.name);
  const bayLocation = locations.find((l) => l.id === bay.locationId);

  function commit() {
    setEditing(false);
    onRename(draft);
  }

  return (
    <div className={cn('flex items-center gap-2 border border-border px-3 py-2', !bay.isActive ? 'opacity-60' : '')}>
      <div className="flex flex-col gap-0.5">
        <button type="button" onClick={onMoveUp} disabled={!canMoveUp} className="text-[10px] leading-none text-muted disabled:opacity-30">
          ▲
        </button>
        <button type="button" onClick={onMoveDown} disabled={!canMoveDown} className="text-[10px] leading-none text-muted disabled:opacity-30">
          ▼
        </button>
      </div>

      {editing ? (
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') {
              setDraft(bay.name);
              setEditing(false);
            }
          }}
          className="h-8 max-w-xs"
        />
      ) : (
        <button
          type="button"
          onClick={() => {
            setDraft(bay.name);
            setEditing(true);
          }}
          className="flex-1 text-left text-sm font-semibold text-ink hover:underline"
        >
          {bay.name}
        </button>
      )}

      {showLocations ? (
        <Select value={bay.locationId} onValueChange={onChangeLocation}>
          {activeLocations.map((loc) => (
            <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
          ))}
          {bayLocation && !bayLocation.isActive ? (
            <SelectItem value={bayLocation.id}>{bayLocation.name}</SelectItem>
          ) : null}
        </Select>
      ) : null}

      <span
        className={cn(
          'rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide',
          bay.isActive ? 'bg-success/15 text-success' : 'bg-surface-2 text-muted',
        )}
      >
        {bay.isActive ? t('bays.active') : t('bays.inactive')}
      </span>

      <Button size="sm" variant="outline" onClick={onToggleActive}>
        {bay.isActive ? t('bays.deactivate') : t('bays.reactivate')}
      </Button>
    </div>
  );
}
